import React, { useState, useEffect } from 'react';
import { Settings, Sliders, Scissors, Maximize, ImageDown, RotateCcw, Save, Trash2 } from 'lucide-react';

const labelClass = "block text-sm font-medium text-gray-300 mb-1";
const selectClass = "w-full p-2.5 rounded-lg bg-slate-700/70 text-gray-200 border border-slate-600 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-colors";
const checkboxLabelClass = "flex items-center gap-2 text-sm text-gray-300 cursor-pointer hover:text-gray-200 transition-colors";
const checkboxClass = "h-4 w-4 text-indigo-500 border-slate-500 rounded focus:ring-indigo-400 bg-slate-600 accent-indigo-500";
const radioClass = "h-4 w-4 text-indigo-500 border-slate-500 focus:ring-indigo-400 bg-slate-600 accent-indigo-500";
const rangeClass = "w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer accent-indigo-500";
const smallBtnClass = "flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/50";

const FILTER_DEFS = [
  { key: 'brightness', label: 'Brightness', unit: '%', min: 0, max: 200, step: 1, defaultVal: 100 },
  { key: 'contrast', label: 'Contrast', unit: '%', min: 0, max: 200, step: 1, defaultVal: 100 },
  { key: 'saturation', label: 'Saturation', unit: '%', min: 0, max: 200, step: 1, defaultVal: 100 },
  { key: 'grayscale', label: 'Grayscale', unit: '%', min: 0, max: 100, step: 1, defaultVal: 0 },
  { key: 'sepia', label: 'Sepia', unit: '%', min: 0, max: 100, step: 1, defaultVal: 0 },
  { key: 'invert', label: 'Invert', unit: '%', min: 0, max: 100, step: 1, defaultVal: 0 },
  { key: 'opacity', label: 'Opacity', unit: '%', min: 0, max: 100, step: 1, defaultVal: 100 },
  { key: 'blur', label: 'Blur', unit: 'px', min: 0, max: 10, step: 0.1, defaultVal: 0 },
  { key: 'hueRotate', label: 'Hue Rotate', unit: 'deg', min: 0, max: 360, step: 1, defaultVal: 0 },
];

const PRESETS_KEY = 'jai_filter_presets';

function loadPresets() {
  try {
    const raw = localStorage.getItem(PRESETS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function savePresetsToStorage(presets) {
  localStorage.setItem(PRESETS_KEY, JSON.stringify(presets));
}

export default function ConversionOptions({
  outputFormat, setOutputFormat,
  jpegQualityValue, setJpegQualityValue,
  filtersEnabled, setFiltersEnabled,
  removeBackground, setRemoveBackground,
  backgroundTolerance, setBackgroundTolerance,
  cropEnabled, setCropEnabled,
  cropSides, updateCropSide,
  autoScale, setAutoScale,
  scaleFactor, setScaleFactor,
  filters, updateFilter, resetFilters
}) {
  const [presets, setPresets] = useState(loadPresets);
  const [presetName, setPresetName] = useState('');

  useEffect(() => {
    savePresetsToStorage(presets);
  }, [presets]);

  const handleSavePreset = () => {
    const name = presetName.trim();
    if (!name) return;
    setPresets(prev => [...prev.filter(p => p.name !== name), { name, filters: { ...filters } }]);
    setPresetName('');
  };

  const handleLoadPreset = (preset) => {
    Object.entries(preset.filters).forEach(([key, value]) => {
      updateFilter(key, value);
    });
    setFiltersEnabled(true);
  };

  const handleDeletePreset = (name) => {
    setPresets(prev => prev.filter(p => p.name !== name));
  };

  const hasActiveFilters = FILTER_DEFS.some(f => filters[f.key] !== f.defaultVal);

  return (
    <div className="w-full bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-700/50 p-6 space-y-6 animate-slide-up">
      <div className="flex items-center gap-2">
        <Settings size={20} className="text-indigo-400" />
        <h2 className="text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-cyan-400">
          Conversion Options
        </h2>
      </div>

      {/* Image Filters — at top for preview visibility */}
      <div className="pt-4 border-t border-slate-700/50">
        <div className="flex items-center justify-between">
          <label className={checkboxLabelClass}>
            <input type="checkbox" checked={filtersEnabled} onChange={(e) => setFiltersEnabled(e.target.checked)} className={checkboxClass} />
            <Sliders size={14} className="text-gray-400" />
            Image Filters
          </label>
          {filtersEnabled && hasActiveFilters && (
            <button
              onClick={resetFilters}
              className={`${smallBtnClass} text-gray-400 hover:text-white bg-slate-700/50 hover:bg-slate-600/50`}
            >
              <RotateCcw size={12} />
              Reset
            </button>
          )}
        </div>

        {filtersEnabled && (
          <div className="mt-3 p-4 bg-slate-700/30 rounded-xl border border-slate-700/50 animate-fade-in space-y-4">
            {/* Filter sliders */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              {FILTER_DEFS.map(f => {
                const isActive = filters[f.key] !== f.defaultVal;
                return (
                  <div key={f.key}>
                    <label htmlFor={`${f.key}Slider`} className="flex items-center justify-between text-xs mb-0.5">
                      <span className={isActive ? 'text-indigo-300 font-medium' : 'text-gray-400'}>
                        {f.label}
                      </span>
                      <span className={`font-mono ${isActive ? 'text-indigo-400' : 'text-gray-500'}`}>
                        {filters[f.key]}{f.unit}
                      </span>
                    </label>
                    <input
                      type="range" id={`${f.key}Slider`}
                      className={`w-full h-1.5 rounded-lg appearance-none cursor-pointer ${isActive ? 'accent-indigo-500 bg-indigo-900/30' : 'accent-slate-500 bg-slate-600'}`}
                      min={f.min} max={f.max} step={f.step}
                      value={filters[f.key]}
                      onChange={e => updateFilter(f.key, f.step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value))}
                    />
                  </div>
                );
              })}
            </div>

            {/* Presets */}
            <div className="pt-3 border-t border-slate-600/50">
              <p className="text-xs text-gray-400 mb-2 font-medium">Filter Presets</p>

              {/* Saved presets */}
              {presets.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {presets.map(p => (
                    <div key={p.name} className="flex items-center gap-0.5">
                      <button
                        onClick={() => handleLoadPreset(p)}
                        className={`${smallBtnClass} bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25 border border-indigo-500/20`}
                      >
                        {p.name}
                      </button>
                      <button
                        onClick={() => handleDeletePreset(p.name)}
                        className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-500 hover:text-red-400 transition-colors"
                        aria-label={`Delete preset ${p.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Save new preset */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSavePreset()}
                  placeholder="Preset name..."
                  className="flex-1 px-2.5 py-1.5 text-xs rounded-lg bg-slate-700/70 text-gray-200 border border-slate-600 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 placeholder-gray-500 transition-colors"
                />
                <button
                  onClick={handleSavePreset}
                  disabled={!presetName.trim() || !hasActiveFilters}
                  className={`${smallBtnClass} bg-indigo-600/80 text-white hover:bg-indigo-500/80 disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  <Save size={12} />
                  Save
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-1">Save current filter settings as a reusable preset.</p>
            </div>
          </div>
        )}
      </div>

      {/* Downscale Factor */}
      <div className="pt-4 border-t border-slate-700/50">
        <div className="flex items-center gap-2 mb-2">
          <ImageDown size={16} className="text-gray-400" />
          <label className={labelClass}>Downscale Factor</label>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {[
            { value: 1, label: 'Original (1x)' },
            { value: 0.5, label: '1/2 Size (50%)' },
            { value: 0.333, label: '1/3 Size (~33%)' },
            { value: 0.25, label: '1/4 Size (25%)' }
          ].map(opt => (
            <label key={opt.value} className={checkboxLabelClass}>
              <input
                type="radio"
                name="scaleFactor"
                value={opt.value}
                checked={scaleFactor === opt.value}
                onChange={(e) => setScaleFactor(parseFloat(e.target.value))}
                className={radioClass}
              />
              {opt.label}
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1">Applies before minimum size check. If downscaling, min size upscale is skipped.</p>
      </div>

      {/* Output Format & BG Removal */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-700/50">
        <div>
          <label htmlFor="outputFormatSelect" className={labelClass}>Output Format</label>
          <select id="outputFormatSelect" value={outputFormat} onChange={(e) => setOutputFormat(e.target.value)} className={selectClass}>
            <option value="jpeg">JPEG — Smaller, No Transparency</option>
            <option value="png">PNG — Lossless, Supports Transparency</option>
            <option value="webp">WebP — Modern, Small & Transparent</option>
            <option value="gif">GIF — Simple Graphics, Animated</option>
            <option value="bmp">BMP — Uncompressed Bitmap</option>
            <option value="ico">ICO — Favicon / Icon Format</option>
          </select>
        </div>
        <div>
          <label className={checkboxLabelClass}>
            <input type="checkbox" checked={removeBackground} onChange={(e) => setRemoveBackground(e.target.checked)} className={checkboxClass} />
            Remove White Background
          </label>
          {removeBackground && (
            <div className="mt-2 pl-6">
              <label htmlFor="bgTolerance" className="block text-xs text-gray-400 mb-1">Tolerance ({backgroundTolerance})</label>
              <input type="range" id="bgTolerance" min="1" max="100" value={backgroundTolerance} onChange={(e) => setBackgroundTolerance(parseInt(e.target.value))} className={rangeClass} />
              <p className="text-xs text-gray-500">Higher = removes more white-ish colors.</p>
            </div>
          )}
        </div>
      </div>

      {/* Crop & Auto Scale */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-700/50">
        <div>
          <label className={checkboxLabelClass}>
            <input type="checkbox" checked={cropEnabled} onChange={(e) => setCropEnabled(e.target.checked)} className={checkboxClass} />
            <Scissors size={14} className="text-gray-400" />
            Crop Image
          </label>
          {cropEnabled && (
            <div className="mt-3 pl-6 space-y-2">
              {[
                { side: 'top', label: 'Top' },
                { side: 'right', label: 'Right' },
                { side: 'bottom', label: 'Bottom' },
                { side: 'left', label: 'Left' }
              ].map(({ side, label }) => (
                <div key={side}>
                  <label htmlFor={`crop-${side}`} className="block text-xs text-gray-400 mb-0.5">
                    {label}: <span className="text-indigo-400 font-mono">{cropSides[side]}px</span>
                  </label>
                  <input
                    type="range" id={`crop-${side}`}
                    min="0" max="1000" step="5"
                    value={cropSides[side]}
                    onChange={(e) => updateCropSide(side, parseInt(e.target.value))}
                    className={rangeClass}
                  />
                </div>
              ))}
              <p className="text-xs text-gray-500">Crop values scale with the downscale factor.</p>
            </div>
          )}
        </div>
        <div>
          <label className={checkboxLabelClass}>
            <input type="checkbox" checked={autoScale} onChange={(e) => setAutoScale(e.target.checked)} className={checkboxClass} />
            <Maximize size={14} className="text-gray-400" />
            Auto-Scale Object
          </label>
          <p className="text-xs text-gray-500 mt-1 pl-6">Trims transparent space and fills the frame.</p>
        </div>
      </div>

      {/* Compression Quality */}
      <div className="pt-4 border-t border-slate-700/50">
        <label htmlFor="jpegQuality" className={labelClass}>
          Compression Quality: <span className="font-mono text-indigo-400">{parseFloat(jpegQualityValue).toFixed(2)}</span>
        </label>
        <input type="range" id="jpegQuality" min="0.1" max="1.0" step="0.05" value={jpegQualityValue} onChange={(e) => setJpegQualityValue(e.target.value)} className={rangeClass} disabled={!['jpeg', 'webp'].includes(outputFormat)} />
        <p className="text-xs text-gray-500 mt-1">{['jpeg', 'webp'].includes(outputFormat) ? 'Lower = smaller file, less quality.' : 'Quality applies to JPEG/WebP only.'}</p>
      </div>
    </div>
  );
}
